import React, { useEffect, useMemo, useState, useCallback } from "react"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Input } from "../../../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import {
  Search,
  Bell,
  Grid,
  List,
  Eye,
  Mail,
  Calendar,
  MoreVertical,
  ArrowUpDown,
  Clock,
  Shield,
  UserIcon,
  Users,
  UserPlus,
  UserCheck,
  Filter,
  RefreshCw,
  Loader2,
  Settings
} from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar"
// @ts-ignore
import InviteUser from "./InviteUsers.tsx";
// @ts-ignore
import { useGlobalUsers } from "../../Services/UserManagement.tsx";
// @ts-ignore
import { useUserContext } from "../../../Contexts/UserProvider.tsx";
import { useHasPermission } from "../../../components/hooks/useHasPermission"
import UserManagementDetailsView from "./UserManagementDetailView"
import UserDetailsSheet from "./UserDetailsSheet"
import { UserGridList } from "./UserGridList";
import { UserTableList } from "./UserTableList";
import { TenantUserTableList } from "./TenantUserTableList";
import { useInvitationManagement } from "../../Services/InvitationManagement";
import { toast } from "react-toastify";
import ConfirmDialog from "../Modals/ConfirmationModal"
import UserRoles from "./UserRoles"
import RoleCreation from "./RoleCreation"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { usePermissionManagement } from "../../Services/PermissionManagement"

const SAMPLE_PERMISSION_CATEGORIES = [
  {
    id: "1",
    name: "Order Management",
    description: "Order and blockchain management",
    permissions: [
      {
        id: "p1",
        key: "order.create",
        resource: "Order",
        action: "Create",
        description: "Create new orders in the system",
      },
      {
        id: "p2",
        key: "order.read",
        resource: "Order",
        action: "Read",
        description: "View order information",
      },
      {
        id: "p3",
        key: "order.update",
        resource: "Order",
        action: "Update",
        description: "Update order details",
      },
    ],
  },
  {
    id: "2",
    name: "Role Management",
    description: "Role management permissions",
    permissions: [
      {
        id: "p5",
        key: "role.create",
        resource: "Role",
        action: "Create",
        description: "Create new roles",
      },
      {
        id: "p6",
        key: "role.read",
        resource: "Role",
        action: "Read",
        description: "View role information",
      },
      {
        id: "p7",
        key: "role.update",
        resource: "Role",
        action: "Update",
        description: "Update role details and permissions",
      },
    ],
  },
  {
    id: "3",
    name: "Product Types",
    description: "Product type management",
    permissions: [
      {
        id: "p9",
        key: "product.create",
        resource: "Product",
        action: "Create",
        description: "Create new product types",
      },
      {
        id: "p10",
        key: "product.read",
        resource: "Product",
        action: "Read",
        description: "View product types",
      },
    ],
  },
]

const SAMPLE_ROLES = [
  {
    id: "1",
    roleName: "Administrator",
    description: "Full system access with all permissions",
    permissionCategories: SAMPLE_PERMISSION_CATEGORIES,
  },
  {
    id: "2",
    roleName: "Editor",
    description: "Can create and manage content",
    permissionCategories: [SAMPLE_PERMISSION_CATEGORIES[2]],
  },
]

// Helper function to get status badge
const getStatusBadge = (status: string) => {
  switch (status) {
    case "Active":
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <UserCheck className="h-3 w-3" />
          Active
        </Badge>
      )
    case "Inactive":
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
          <UserIcon className="h-3 w-3" />
          Inactive
        </Badge>
      )
    case "Pending":
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      )
    case "Read":
      return (
        <Badge variant="outline" className="bg-green-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Read
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-800 flex items-center gap-1">
          {status}
        </Badge>
      )
  }
}

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// Helper function to get user avatar
const getUserAvatar = (email: string) => {
  const initials = email ? email.substring(0, 2).toUpperCase() : "??"

  return (
    <Avatar className="h-10 w-10 border">
      <AvatarImage src="/placeholder.svg" alt={email || "User"} />
      <AvatarFallback className="bg-purple-100 text-purple-800">{initials}</AvatarFallback>
    </Avatar>
  )
}

// Helper function to extract name from email
const getNameFromEmail = (email: string) => {
  if (!email) return "Unknown User"
  const parts = email.split("@")[0].split(".")
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
    .replace(/[0-9]/g, "")
}

const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<"grid" | "list">("list")
  const [statusFilter, setStatusFilter] = useState("All Status")
  const [typeFilter, setTypeFilter] = useState("All Types")

  const [isShow, setIsShow] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const { getUsers, getUsersStatistics, getTenantUsers } = useGlobalUsers();
  const { resendInvitation } = useInvitationManagement();
  const { getAllPermissionCategory, getAllRoles } = usePermissionManagement();
  const { tenant } = useUserContext();
  const [roles, setRoles] = useState([])
  const [editingRole, setEditingRole] = useState(null)
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTenantUsers, setIsLoadingTenantUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedTenantUser, setSelectedTenantUser] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tenantDetailsOpen, setTenantDetailsOpen] = useState(false);
  const [initialTab, setInitialTab] = useState("details");
  const [tenantInitialTab, setTenantInitialTab] = useState("details");
  const [isDropdownOpen, setIsDropdownOpen] = useState<string | null>(null);
  const [isRoleCreation, setIsRoleCreation] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const canInviteUser = useHasPermission("UserManagement.Invite");
  const canCreateRole = useHasPermission("RoleManagement.Create");
  const canViewRoles = useHasPermission("RoleManagement.View");

  const [confirmationModal, setConfirmationModal] = useState({
    isShow: false,
    onConfirm: () => { },
  });

  const handleInviteUser = () => {
    setIsShow(!isShow);
  };

  useEffect(() => {
    getTenantStats();
    getAllInvitations();
    fetchTenantUsers();
    getAllUserPermissions();
    getAllRolesData();
  }, []);

  const getTenantStats = async () => {
    const tenantStats = await getUsersStatistics(tenant?.id);
    setStats(tenantStats);
  };

  const getAllUserPermissions = async () => {
    const allPermissions = await getAllPermissionCategory();
    setPermissions(allPermissions);
  };

  const getAllInvitations = async () => {
    setIsLoading(true);
    try {
      const response: any = await getUsers(tenant?.id, "isUserManagement");
      setInvitations(response);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const fetchTenantUsers = async () => {
    setIsLoadingTenantUsers(true);
    try {
      const response: any = await getTenantUsers(tenant?.id);
      const seen = new Set<string>();
      const uniqueUsers = (response || []).filter((user) => {
        if (!user?.id || seen.has(user.id)) return false;
        seen.add(user.id);
        return true;
      });
      setTenantUsers(uniqueUsers);
      setIsLoadingTenantUsers(false);
    } catch (error) {
      setIsLoadingTenantUsers(false);
    }
  };

  const successCallBack = () => {
    getTenantStats();
    getAllInvitations();
    fetchTenantUsers();
    getAllRolesData();
  }

  // Filter invitations based on search query, status, and type
  const filteredInvitations = invitations.filter(
    (user) =>
      (user?.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user?.invitedFromUserName?.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (statusFilter === "All Status" || user.notificationStatus === statusFilter) &&
      (typeFilter === "All Types" || user.userType === typeFilter),
  )

  // Filter tenant users based on search query
  const filteredTenantUsers = tenantUsers.filter(
    (user) =>
      user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user?.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Get unique statuses and types for filters
  const statuses = ["All Status", ...Array.from(new Set(invitations.map((user) => user.notificationStatus)))]
  const types = ["All Types", ...Array.from(new Set(invitations.map((user) => user.userType)))]

  // Simulate refresh data
  const handleRefresh = () => {
    setIsLoading(true);
    setIsLoadingTenantUsers(true);
    successCallBack();
    setTimeout(() => {
      setIsLoading(false)
      setIsLoadingTenantUsers(false)
    }, 1000)
  };

  // Handle opening invitation details (Invitations tab)
  const handleViewUser = (user) => {
    setIsDropdownOpen(null);
    setSelectedUser(user)
    setInitialTab("details")
    setDetailsOpen(true)
  }

  const handleViewTenantUser = useCallback((user) => {
    setSelectedTenantUser(user)
    setTenantInitialTab("details")
    setTenantDetailsOpen(true)
  }, []);

  const handleAssignRoleAction = useCallback((user) => {
    setSelectedTenantUser(user)
    setTenantInitialTab("permission")
    setTenantDetailsOpen(true)
  }, []);

  const handleResendInvitation = async (id) => {
    try {
      await resendInvitation(id);
      toast.success("Invitation resent successfully!");
      successCallBack();
    } catch (error) {
      toast.error("Failed to resend the invitation. Please try again.");
      console.error("Error resending invitation: ", error);
    }
  };

  const handleConfirmationModal = (isOpen, onConfirm) => {
    setConfirmationModal({
      isShow: isOpen,
      onConfirm: onConfirm,
    });
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
  }

  const getAllRolesData = () => {
    getAllRoles()
      .then((res) => {
        setRoles(res);
      })
      .catch((err) => {
        console.error("Error fetching roles:", err);
      });
  };

  const getUserTypeBadge = useCallback((roleid: string) => {
    const roleName = roles.find((role) => role.id === roleid)?.roleName || roleid;
    return (
      <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
        <Shield className="h-3 w-3" />
        {roleName}
      </Badge>
    )
  }, [roles]);

  return (
    <div className="w-full mx-auto p-6 space-y-6 user-management-section">
      <main className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">User Management</h2>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
              className={isLoading ? "opacity-50 cursor-not-allowed" : ""}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
            {canCreateRole && (
              <Button className="bg-primary hover:bg-[#8e44ad] text-white" onClick={() => setIsRoleCreation(true)}>
                <Settings className="h-4 w-4 mr-2" /> Create User Role
              </Button>
            )}
            {canInviteUser && (
              <Button className="bg-primary hover:bg-[#8e44ad] text-white" onClick={handleInviteUser}>
                <UserPlus className="h-4 w-4 mr-2" /> Add User
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-purple-600 mr-2" />
                <span className="text-2xl font-bold">{stats?.totalUserCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <UserCheck className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-2xl font-bold">{stats?.activeUserCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Pending Invitations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-2xl font-bold">
                  {stats?.pendingUserCount}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="users" className="w-full mb-6">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="invitations">Invitations</TabsTrigger>
            {canViewRoles && <TabsTrigger value="roles">Roles</TabsTrigger>}
          </TabsList>
          <TabsContent value="users">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search active users..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <TenantUserTableList
              users={filteredTenantUsers}
              isLoading={isLoadingTenantUsers}
              getUserTypeBadge={getUserTypeBadge}
              handleViewTenantUser={handleViewTenantUser}
              handleAssignRoleAction={handleAssignRoleAction}
            />
          </TabsContent>
          <TabsContent value="invitations">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search users by email or name..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  className={viewMode === "grid" ? "bg-primary hover:bg-[#8e44ad]" : ""}
                  onClick={() => setViewMode("grid")}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  className={viewMode === "list" ? "bg-primary hover:bg-[#8e44ad]" : ""}
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {viewMode === "grid" ? (
              <UserGridList
                users={filteredInvitations}
                isLoading={isLoading}
                getUserAvatar={getUserAvatar}
                getNameFromEmail={getNameFromEmail}
                handleViewUser={handleViewUser}
                handleConfirmationModal={handleConfirmationModal}
                resendInvitation={resendInvitation}
                getUserTypeBadge={getUserTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            ) : (
              <UserTableList
                users={filteredInvitations}
                isLoading={isLoading}
                getUserAvatar={getUserAvatar}
                getNameFromEmail={getNameFromEmail}
                handleViewUser={handleViewUser}
                handleConfirmationModal={handleConfirmationModal}
                resendInvitation={resendInvitation}
                getUserTypeBadge={getUserTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </TabsContent>
          {canViewRoles && (
            <TabsContent value="roles">
              <UserRoles roles={roles} permissionCategories={permissions} onRoleChange={getAllRolesData} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      <InviteUser isShow={isShow} setIsShow={setIsShow} successCallBack={successCallBack} roles={roles}></InviteUser>

      <RoleCreation open={isRoleCreation} onOpenChange={setIsRoleCreation} permissionCategories={permissions} onRoleChange={() => getAllRolesData()} />

      <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to continue ?" description="" confirmText="Confirm" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />

      {selectedUser && <UserManagementDetailsView user={selectedUser} open={detailsOpen} onOpenChange={setDetailsOpen} resendCallBack={handleResendInvitation} allRoles={roles} initialTab={initialTab} />}

      {selectedTenantUser && (
        <UserDetailsSheet
          user={selectedTenantUser}
          open={tenantDetailsOpen}
          onOpenChange={setTenantDetailsOpen}
          allRoles={roles}
          initialTab={tenantInitialTab}
        />
      )}
    </div>
  )
}

export default UserManagement;